import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface PlatformAuthUser {
  id: number;
  firstName: string;
  lastName: string;
  language: string;
}

export interface PlatformAuthResponse {
  jwt: string;
  user: PlatformAuthUser;
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationAdapter {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/candidate/auth`;

  /**
   * Logs in a candidate with email and password.
   * @param credentials - Login credentials and session length
   * @returns Observable with JWT and user data
   */
  login(credentials: {
    email: string;
    password: string;
    rememberMe: boolean;
    tfa?: string;
  }): Observable<PlatformAuthResponse> {
    return this.http.post<PlatformAuthResponse>(`${this.base}/login`, credentials);
  }

  /**
   * Registers a new candidate account (email-first, password is set via mail link).
   * @param payload - Registration payload
   * @returns Observable that completes when registration succeeds
   */
  register(payload: {
    email: string;
    captchaCode: string;
    termsAccepted: boolean;
    talenthubAccepted: boolean;
  }): Observable<void> {
    return this.http.post<void>(`${this.base}/register`, payload);
  }

  /**
   * Logs out the current candidate session.
   * @returns Observable that completes when logout succeeds
   */
  logout(): Observable<void> {
    return this.http.post<void>(`${this.base}/logout`, {});
  }

  /**
   * Requests a password reset email.
   * @param payload - Password reset payload
   * @returns Observable that completes when request succeeds
   */
  passwordReset(payload: { email: string; captchaCode: string }): Observable<void> {
    return this.http.post<void>(`${this.base}/password-reset`, payload);
  }

  /**
   * Sets a new password using a valid nonce.
   * @param payload - Set password payload
   * @returns Observable that completes when password is set
   */
  setPassword(payload: { nonce: string; password: string }): Observable<void> {
    return this.http.post<void>(`${this.base}/set-password`, payload);
  }

  /**
   * Returns required login factors for an email without revealing the account.
   * @param email - Login email
   * @returns Observable with TOTP type list
   */
  getAuthenticationMethods(email: string): Observable<{ types: string[] }> {
    return this.http.get<{ types: string[] }>(`${this.base}/login-authentication-methods`, {
      params: { email },
    });
  }

  /**
   * Resends the email verification mail.
   * @param payload - Send verification payload
   * @returns Observable that completes when request succeeds
   */
  sendEmailVerification(payload: { email: string; captchaCode: string }): Observable<void> {
    return this.http.post<void>(`${this.base}/send-email-verification`, payload);
  }

  /**
   * Verifies a candidate email using a nonce.
   * @param payload - Verification payload
   * @returns Observable that completes when verification succeeds
   */
  emailVerification(payload: { nonce: string }): Observable<void> {
    return this.http.post<void>(`${this.base}/email-verification`, payload);
  }

  /**
   * Refreshes the JWT using the httpOnly refresh token cookie.
   * @returns Observable with new JWT
   */
  refreshToken(): Observable<{ jwt: string }> {
    return this.http.post<{ jwt: string }>(`${this.base}/refresh-token`, {});
  }

  /**
   * Signs in a candidate with Google SSO.
   * @param payload - Google ID token, session length and registration consents
   * @returns Observable with JWT and user data
   */
  signInWithGoogle(payload: {
    token: string;
    rememberMe: boolean;
    termsAccepted?: boolean;
    talenthubAccepted?: boolean;
  }): Observable<PlatformAuthResponse> {
    return this.http.post<PlatformAuthResponse>(`${this.base}/google-authentication`, payload);
  }
}
